import {
  Breadcrumbs,
  IconButton,
  Link,
  Tooltip,
  Typography
} from '@material-ui/core';
import { SvgIconProps } from '@material-ui/core/SvgIcon';
import { NavigateNextRounded } from '@material-ui/icons';
import { ReactElement, ReactNode } from 'react';
import { Link as RRLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useDeviceScreen from '../../hooks/useDeviceScreen';
import { routes } from './config';

interface BreadcrumbProps {
  name: string;
  data?: { [key: string]: any };
  icon?: ReactElement<SvgIconProps>;
  to?: string;
  titleTooltip?: string;
}

interface Route {
  name: string;
  routeName: string;
  path: string;
  children?: Route[];
}

function generateLink(
  name: string,
  data: { [key: string]: any } = {},
  routes: Route[],
  t: (key: string) => string,
  icon?: ReactElement<SvgIconProps>,
  to: string = '/',
  titleTooltip: string = ''
) {
  const links: ReactNode[] = [];
  //Select the fisrt object in routes array
  let [currentRoute] = routes;
  const routeName = currentRoute.routeName;
  // Replace display text if data has match routeName (if not return translated name)
  const breadcrumbName = data[routeName] ? data[routeName] : t(currentRoute.name);

  // Create plain text for last breadcrumb and stop if cannot find children property
  if (name === routeName) {
    const text = (
      <Typography
        key={routeName}
        component="span"
        variant="body1"
        color="textPrimary"
      >
        {breadcrumbName}
        {icon && (
          <Tooltip title={titleTooltip} arrow>
            <IconButton
              style={{ marginTop: -8, marginBottom: -8, marginLeft: 4 }}
              component={RRLink}
              to={to}
              size="small"
            >
              {icon}
            </IconButton>
          </Tooltip>
        )}
      </Typography>
    );
    links.push(text);
    return links;
  } else if (!currentRoute.children) {
    return [];
  }

  //Create link property
  const [mainPath, queryString] = currentRoute.path.split('?');
  const pathChunks = mainPath.split('/');
  pathChunks.forEach((pathChunk: string, index: number) => {
    // Replace :text with Id from data
    if (pathChunk.startsWith(':')) {
      const propertyId = pathChunk.substring(1);
      pathChunks[index] = data[propertyId];
    }
  });

  const queryPathChunks = queryString?.split('&');
  queryPathChunks?.forEach((queryPathChunk: string, index: number) => {
    // Replace :text with Id from data
    if (queryPathChunk.startsWith(':')) {
      const propertyId = queryPathChunk.substring(1);
      queryPathChunks[index] = `${propertyId}=${data[propertyId]}`;
    }
  });

  // Create link text for each breadcrumb
  const link = (
    <Link
      key={routeName}
      color="inherit"
      to={[pathChunks.join('/'), queryPathChunks?.join('&')]?.join('?')}
      component={RRLink}
    >
      {breadcrumbName}
    </Link>
  );

  // Choose a path
  links.push(link);

  currentRoute.children.forEach((childRoute: any) => {
    const childLinks = generateLink(
      name,
      data,
      [childRoute],
      t,
      icon,
      to,
      titleTooltip
    );
    links.push(...childLinks);
  });

  // Unchoose if it's not the right path
  if (links.length === 1) {
    links.pop();
  }

  return links;
}

export default function Breadcrumb({
  name,
  data = {},
  icon,
  to = '/',
  titleTooltip = ''
}: BreadcrumbProps) {
  const { IS_TABLET } = useDeviceScreen();
  const { t } = useTranslation();

  return (
    <Breadcrumbs
      maxItems={IS_TABLET ? 3 : 5}
      itemsAfterCollapse={IS_TABLET ? 2 : 4}
      separator={<NavigateNextRounded fontSize="small" />}
      aria-label="navigation"
    >
      {generateLink(name, data, routes, t, icon, to, titleTooltip)}
    </Breadcrumbs>
  );
}

// How to use Breadcrumb component
// <Breadcrumb name="documentName" data={{ projectName, projectId, documentName, documentId }} />
// Home > Project A > Isometric drawing
